<template>
  <v-card id="rate-sheet-editor">
    <v-card-title :class="contractTypeColor">
      <v-row align="center">
        <v-col>
          <span class="headline">
            {{ componentTitle }}
          </span>
        </v-col>

        <v-col cols="auto" v-if="dialogInitialized">
          <BaseDialogActions @close="handleCloseWithEdits" hideRefresh elementId="rate-sheet-editor-actions"/>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text class="pt-5">
      <v-row align="center" justify="space-between" v-if="!hideDefinition">
        <v-col cols="12">
          <span class="subtitle-1">{{ $t('selectRateSheetDefinition') }}</span>
        </v-col>
        <v-col cols="12" sm="6" lg="4">
          <LoadableComponent :loading="!initialized">
            <RateSheetDefinitionAutocomplete
              class="mb-n3"
              :propRateSheetDefinitions="rateSheetDefinitionsFiltered"
              :propSelection="selectedRateSheetDefinition"
              :clearable="false"
              @chosen="rateSheetDefnChosen"
              @chosen-automatic="rateSheetDefnChosen"
            />
          </LoadableComponent>
        </v-col>
      </v-row>

      <v-row justify="end" align="center">
        <v-col cols="12" v-if="(currentVersion || loading) && !hideDefinition">
          <span class="subtitle-1">{{ $t(editing ? 'editRateSheet' : 'createRateSheet') }}</span>
        </v-col>

        <v-col cols="12" v-if="lineItems">
          <LoadableComponent :loading="loading || !initialized">
            <RateSheetLineTableFilterable
              :rateSheetInteractionMode="editing ? RateSheetInteractionMode.RateSheet.Edit : RateSheetInteractionMode.RateSheet.Create"
              :newLineDisabled="newLineDisabled"
              :lines="lineItems"
              :accountId="accountId"
              :loading="loading"
              :tableHeightCalculationData="tableHeightCalculationData"
              :crud="true"
              @rates-edited="handleRatesEdited"
              @edit="editClicked"
              @lock="lockToggled"
              @remove="removeClicked"
              @delete="deleteClicked"
              @new="newLineClicked"
              @group-by-species-group-toggled="groupBySpeciesGroupToggled"
              @restore-ratesheet-lines="restoreRateSheetLines"
            />
          </LoadableComponent>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="px-1 pb-5">
      <v-row justify="space-between">
        <v-col cols="auto">
          <v-btn outlined text color="secondary" @click="onCancel" id="cancel"> {{ $t('cancel') }} </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-btn :class="contractTypeColor" @click="onConfirm" id="confirm"> {{ $t('confirm') }} </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>

    <Dialog :stateId="dialogId" @close="closeDialog" @click:outside="closeDialog" :fullscreenBreakPoint="'xs'">
      <RateSheetLineForm
        v-if="useRateSheetLineForm"
        @confirm="rateChanged"
        :rateSheetLine="mutatingLine"/>

      <AccountRateSheetDefinitionLineForm
        v-if="useRateSheetDefinitionLineForm"
        isRateSheetLevel
        :rateSheetDefinitionLine="typeof mutatingLine === 'object' ? mutatingLine : undefined"
        :account="{ accountId }"
        :rateSheetDefinition="selectedRateSheetDefinition"
        :selectedVersion="rateSheetBuilder"
        @save="handleSaveLine"
        @save-and-close="handleSaveLineAndClose"
        @cancel="closeDialog"
      />

      <ConfirmDialog
        v-if="confirmCloseDialog"
        :title="$t('closeDialogWithoutSavingTitle')"
        :body="$t('closeDialogWithoutSavingWarning')"
        :confirmText="$t('close')"
        :cancelText="$t('cancel')"
        width="35vw"
        @cancel="cancelConfirmDialog"
        @confirm="confirmUnsavedChangesDialog"
      />
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ContractMode } from '@/utils/Enumerations'
import { cloneDeep } from 'lodash'
import { getActiveRateSheetVersion, rateSheetDefinitionLineSorter } from '@/components/account/account-scaling/rate-sheet-definitions/RateSheetDefinitions'
import { uniqueDialogId, colorClassForContractType } from '@/utils/componentHelpers'
import moment from 'moment'
import { uuidv4 } from '@/utils/NumericMutations.js'
import { RateSheetInteractionMode, RateSheetLineBuilder } from '@/components/activity/rate-sheet/RateSheet'
import RateSheet from '@/model/RateSheet.js'

const LINE_EDITOR_WIDTH = '500px'

export default {
  name: 'RateSheetEditor',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    RateSheetDefinitionAutocomplete: () => import('@/components/account/account-scaling/rate-sheet-definitions/RateSheetDefinitionAutocomplete.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    RateSheetLineForm: () => import('./RateSheetLineForm.vue'),
    AccountRateSheetDefinitionLineForm: () => import('@/components/account/account-scaling/rate-sheet-definitions/AccountRateSheetDefinitionLineForm.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    RateSheetLineTableFilterable: () => import('@/components/activity/rate-sheet/RateSheetLineTableFilterable.vue'),
    Dialog: () => import('@/components/Dialog.vue')
  },

  props: {
    accountId: {
      type: Number,
      required: true
    },
    initDefinition: {
      type: Object,
      required: false,
      default: undefined
    },
    existingRateSheet: {
      type: Object,
      required: false,
      default: undefined
    },
    tableHeightCalculationData: {
      type: Object,
      required: false,
      default: () => ({ auto: true })
    },
    activity: {
      type: Object,
      required: false,
      default: undefined
    },
    contract: {
      type: Object,
      required: false,
      default: undefined
    },
    hideDefinition: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    selectedRateSheetDefinition: null,
    currentVersion: null,
    versionsByRateSheetDefinitionId: new Map(),
    loading: false,
    dialogInitialized: false,
    rateSheetBuilder: null,
    dialogId: uniqueDialogId('rate-sheet-editor'),
    mutatingLine: null,
    initialized: false,
    groupBySpeciesGroup: false,
    ContractMode,
    RateSheetInteractionMode,
    confirmCloseDialog: false
  }),

  computed: {
    ...mapGetters('rate-sheet-definition', ['accountRateSheetDefinitions']),
    ...mapGetters('account-scaling', ['accountLengthGroups', 'accountIdOfLengthGroups',
      'accountDiameterGroups', 'accountIdOfDiameterGroups',
      'accountSpeciesGroups']),

    contractTypeColor () {
      return colorClassForContractType(this.contract?.type)
    },

    editing () {
      return this.existingRateSheet !== null && this.existingRateSheet !== undefined
    },

    rateSheetDefinitionsFiltered () {
      return this.accountRateSheetDefinitions
    },

    lineItems () {
      if (!this.rateSheetBuilder) return []
      return this.rateSheetBuilder.rateSheetLines.toSorted(rateSheetDefinitionLineSorter)
    },

    componentTitle () {
      let title = this.editing
        ? this.$t('editRateSheet')
        : this.$t('createRateSheet')

      if (this.activity?.activityTemplateName) {
        title = `${title} (${this.activity.activityTemplateName})`
      } else if (this.activity?.activityTemplate?.name) {
        title = `${title} (${this.activity.activityTemplate.name})`
      }
      return title
    },

    useRateSheetLineForm () {
      return this.mutatingLine && this.mutatingLine.rateSheetLineId
    },

    useRateSheetDefinitionLineForm () {
      return this.mutatingLine && !this.mutatingLine.rateSheetLineId
    },

    newLineDisabled () {
      const creatingRateSheet = this.existingRateSheet === null || this.existingRateSheet === undefined
      const noDefinitionSelected = !this.selectedRateSheetDefinition
      return creatingRateSheet && noDefinitionSelected
    }
  },

  async mounted () {
    await this.initialize()
    await this.initializeAccountScalingSpecs()
    this.autoFocus()
  },

  watch: {
    async initDefinition () {
      if (this.initDefinition) {
        this.selectedRateSheetDefinition = this.initDefinition
        this.currentVersion = getActiveRateSheetVersion(await this.getLocalVersionsForSelectedRateSheetDefinition())
      }
    }
  },

  methods: {
    ...mapActions('dialog', ['closeDialogsAtOrAbove', 'openOrUpdateDialog']),
    ...mapActions('rate-sheet-definition', ['fetchVersionedRateSheetDefinition']),
    ...mapActions('account-scaling', ['fetchAccountLengthGroups', 'fetchAccountDiameterGroups', 'fetchAccountSpeciesGroups']),

    async initialize () {
      if (this.existingRateSheet) {
        await this.initByExistingRateSheet()
      } else if (this.initDefinition) {
        await this.initByRateSheetDefinition()
      } else {
        await this.initByDefaultStrategy()
      }
      this.initialized = true
    },

    async initByRateSheetDefinition () {
      await this.rateSheetDefnChosen(this.initDefinition)
    },

    async initByExistingRateSheet () {
      const sheet = this.existingRateSheet
      this.currentVersion = sheet
      this.selectedRateSheetDefinition = {
        rateSheetDefinitionId: sheet.rateSheetDefinitionId,
        name: sheet.name ?? sheet.rateSheetDefinition?.name
      }
      this.assignRateSheetBuilderFromRateSheet(sheet)
    },

    async initByDefaultStrategy () {
      if (this.accountRateSheetDefinitions?.length === 1) {
        await this.rateSheetDefnChosen(this.accountRateSheetDefinitions[0])
      } else {
        this.selectedRateSheetDefinition = null
        this.currentVersion = null
      }
    },

    async initializeAccountScalingSpecs () {
      if (this.accountIdOfLengthGroups !== this.accountId) {
        await this.fetchAccountLengthGroups(this.accountId)
      }
      if (this.accountIdOfDiameterGroups !== this.accountId) {
        await this.fetchAccountDiameterGroups(this.accountId)
      }
      if (this.accountSpeciesGroups.length === 0 || this.accountSpeciesGroups[0].accountId !== this.accountId) {
        await this.fetchAccountSpeciesGroups(this.accountId)
      }
    },

    async rateSheetDefnChosen (rateSheetDefinition) {
      this.selectedRateSheetDefinition = rateSheetDefinition
      if (rateSheetDefinition) {
        this.currentVersion = getActiveRateSheetVersion(await this.getRateSheetVersionsForSelectedDefinition())
        this.assignRateSheetBuilderFromRateSheetDefinition()
      } else {
        this.currentVersion = null
        this.rateSheetBuilder = null
      }
      this.autoFocus()
    },

    async getRateSheetVersionsForSelectedDefinition () {
      let rates = this.getLocalVersionsForSelectedRateSheetDefinition()
      if (rates) {
        return rates
      } else {
        rates = await this.fetchRateSheetDefnVersions()
        this.versionsByRateSheetDefinitionId.set(this.selectedRateSheetDefinition.rateSheetDefinitionId, rates)
        return rates
      }
    },

    async fetchRateSheetDefnVersions () {
      this.loading = true
      const result = await this.fetchVersionedRateSheetDefinition({
        accountId: this.accountId,
        rateSheetDefinitionId: this.selectedRateSheetDefinition.rateSheetDefinitionId
      })
      this.loading = false
      return result
    },

    getLocalVersionsForSelectedRateSheetDefinition () {
      return this.versionsByRateSheetDefinitionId.get(this.selectedRateSheetDefinition?.rateSheetDefinitionId)
    },

    assignRateSheetBuilderFromRateSheetDefinition () {
      const builder = cloneDeep(this.currentVersion)
      builder.rateSheetLines = builder.rateSheetDefinitionLines
      builder.rateSheetLines.forEach(line => {
        line.uuid = uuidv4()
        line.isLocked = false
        line.isDeleted = false
        line.isChanged = false
        line.rateFromDefinition = line.rate
      })
      delete builder.rateSheetDefinitionLines
      this.rateSheetBuilder = builder
    },

    assignRateSheetBuilderFromRateSheet (sheet) {
      const builder = cloneDeep(sheet)
      builder.rateSheetLines = builder.rateSheetLines || []
      builder.rateSheetLines.forEach(line => {
        line.rateFromPreexistingSheet = line.rate
      })
      this.rateSheetBuilder = builder
    },

    autoFocus () {
      if (this.selectedRateSheetDefinition) {
        this.focusAutocomplete()
      } else {
        this.initializeOnDelay()
      }
    },

    focusAutocomplete () {
      setTimeout(() => {
        const e = document.querySelector('#rate-sheet-editor #rate-sheet-definition-autocomplete')
        if (e) e.focus()
        this.dialogInitialized = true
      }, 128)
    },

    initializeOnDelay () {
      setTimeout(() => {
        this.dialogInitialized = true
      }, 128)
    },

    onConfirm () {
      this.emitConfirm()
    },

    emitConfirm () {
      const rateSheet = this.getRateSheet()
      this.$emit('confirm', rateSheet)
    },

    getRateSheet () {
      return new RateSheet(
        this.existingRateSheet?.rateSheetId,
        this.currentVersion?.name || this.selectedRateSheetDefinition?.name || '',
        this.rateSheetBuilder.rateSheetLines.filter(l => !l.isDeleted),
        moment(),
        null,
        this.selectedRateSheetDefinition.rateSheetDefinitionId)
    },

    onCancel () {
      this.$emit('cancel')
    },

    closeDialog () {
      this.mutatingLine = null
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    openDialog (width = LINE_EDITOR_WIDTH) {
      this.openOrUpdateDialog({ id: this.dialogId, width })
    },

    newLineClicked () {
      this.$emit('editing-ratesheet')
      this.mutatingLine = true
      this.openDialog()
    },

    editClicked ({ line, unformattedLine }) {
      this.mutatingLine = this.getCorrespondingLine(line) || unformattedLine
      this.openDialog()
    },

    getCorrespondingLine (line) {
      return this.rateSheetBuilder.rateSheetLines.find(l => (
        (!!line.rateSheetDefinitionLineId && l.rateSheetDefinitionLineId === line.rateSheetDefinitionLineId) ||
        (!!line.rateSheetLineId && l.rateSheetLineId === line.rateSheetLineId) ||
        (!!line.uuid && l.uuid === line.uuid)
      )) || null
    },

    removeClicked ({ line, unformattedLine }) {
      const l = this.getCorrespondingLine(line) || unformattedLine
      l.isDeleted = !l.isDeleted
      l.rowClass = l.isDeleted ? 'deleted' : ''
      l.isChanged = !l.isChanged
    },

    deleteClicked ({ line, unformattedLine }) {
      this.rateSheetBuilder.rateSheetLines = this.rateSheetBuilder.rateSheetLines
        .filter(l => {
          const correspondingLine = this.getCorrespondingLine(line) || unformattedLine
          return l !== correspondingLine
        })
    },

    rateChanged (rate) {
      if (this.mutatingLine) {
        this.mutatingLine.rate = rate
        this.mutatingLine.isChanged = true
      }
      this.closeDialog()
    },

    handleSaveLine (line) {
      const existingLine = this.rateSheetBuilder.rateSheetLines
        .find(l => (!!line.rateSheetDefinitionId && l.rateSheetDefinitionLineId === line.rateSheetDefinitionLineId) ||
          (!!line.uuid && l.uuid === line.uuid))

      if (existingLine) {
        this.handleSaveExistingLine(existingLine, line)
      } else {
        this.handleSaveNewLine(line)
      }
    },

    handleSaveExistingLine (existingLine, line) {
      existingLine.speciesGroup = line.speciesGroup
      existingLine.lengthGroup = line.lengthGroup
      existingLine.diameterGroup = line.diameterGroup
      existingLine.grade = line.grade
      existingLine.endUseSort = line.endUseSort
      existingLine.rate = line.rate
      existingLine.isChanged = true
    },

    handleSaveNewLine (line) {
      const newLine = new RateSheetLineBuilder(
        null,
        line.rate,
        line.speciesGroup,
        line.lengthGroup,
        line.diameterGroup,
        line.grade,
        line.endUseSort
      )
      newLine.isChanged = true
      newLine.uuid = uuidv4()
      this.rateSheetBuilder.rateSheetLines.push(newLine)
    },

    handleSaveLineAndClose (line) {
      this.handleSaveLine(line)
      this.closeDialog()
    },

    lockToggled ({ line }) {
      const l = this.rateSheetBuilder.rateSheetLines
        .find(l => (!!line.rateSheetDefinitionLineId && l.rateSheetDefinitionLineId === line.rateSheetDefinitionLineId) ||
          (!!line.rateSheetLineId && l.rateSheetLineId === line.rateSheetLineId) ||
          (!!line.uuid && l.uuid === line.uuid))
      l.isLocked = !l.isLocked
    },

    groupBySpeciesGroupToggled (value) {
      this.groupBySpeciesGroup = value
    },

    isLineEligible (line) {
      if (!this.shouldFilterByEligibleSpecies) return true
      const speciesGroupId = line.speciesGroupId ?? line.speciesGroup?.speciesGroupId
      if (!speciesGroupId) return true

      return this.eligibleSpeciesGroupIds.includes(speciesGroupId)
    },
    handleRatesEdited (lines) {
      this.$emit('editing-ratesheet')
      lines.forEach(editedLine => {
        const existingLine = this.rateSheetBuilder.rateSheetLines.find(l => {
          return (!!editedLine.rateSheetDefinitionLineId && l.rateSheetDefinitionLineId === editedLine.rateSheetDefinitionLineId) ||
                 (!!editedLine.rateSheetLineId && l.rateSheetLineId === editedLine.rateSheetLineId) ||
                 (!!editedLine.uuid && l.uuid === editedLine.uuid)
        })
        if (existingLine) {
          existingLine.rate = editedLine.rate
          existingLine.isChanged = true
        }
      })
    },

    handleCloseWithEdits () {
      this.confirmCloseDialog = true
      this.openDialog()
    },

    cancelConfirmDialog () {
      this.confirmCloseDialog = false
      this.closeDialog()
    },

    confirmUnsavedChangesDialog () {
      this.confirmCloseDialog = false
      this.$emit('close')
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    restoreRateSheetLines (originalLines) {
      this.rateSheetBuilder.rateSheetLines = originalLines.map(line => ({ ...line }))
    }
  }
}
</script>

<style>
#rate-sheet-editor tr.deleted td {
  color: rgba(0, 0, 0, 0.4);
  text-decoration: line-through;
}
</style>
